// Document detail dialog — opens when a card is clicked. Shows every
// attachment, the full description, approval history, and the list of
// users who have opened this doc (with timestamps). Auto-marks-read on
// open (server-side via GET /documents/{id}?mark_read=true).

import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { toast } from "sonner";
import {
  X, Download, ExternalLink, Eye, Clock, CheckCircle2, XCircle, RefreshCcw,
  ShieldAlert, Users as UsersIcon, Globe2, Highlighter, MessageSquare, Trash2,
  AtSign, CornerDownRight, AlertTriangle, HelpCircle, Search as SearchIcon,
  Pencil,
} from "lucide-react";
import { fmtDateTimeIST } from "../../lib/datetime";
import { STATUS_META, TAG_META, HIGHLIGHT_COLORS, fmtSize, attachmentFlavor, fmtCountdown } from "./docConstants";

export default function DocDetailDialog({
  open, doc, onClose,
  currentUserId = "", isMaster = false,
  users = [], context = "feed",
  onEdit, onRemove,
}) {
  const [full, setFull] = useState(doc);
  const [busy, setBusy] = useState(false);

  // Reset local view whenever a different doc is opened. This runs BEFORE
  // the fetch so the dialog paints instantly with the card-level data.
  useEffect(() => {
    setFull(doc || null);
  }, [doc?.id]);

  useEffect(() => {
    if (!open || !doc?.id) return;
    let cancelled = false;
    setBusy(true);
    api.get(`/documents/${doc.id}?mark_read=true`)
      .then((r) => { if (!cancelled) setFull(r.data); })
      .catch((e) => { if (!cancelled) toast.error(formatError(e)); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [open, doc?.id]);

  if (!doc) return null;
  const d = full || doc;
  const sMeta = STATUS_META[d.status] || STATUS_META.approved;
  const tagMeta = TAG_META[d.tag] || TAG_META.other;
  const tagLabel = d.tag === "other" && d.tag_custom ? d.tag_custom : tagMeta.label;
  const SIcon = sMeta.Icon;
  // Once published to the wider audience, reviewer feedback must NOT be
  // visible in the Documents tab (public feed). It is kept internal to
  // the review workflow only.
  const isPublished = d.status === "approved";
  const iAmReviewer = (d?.reviewers || []).some((r) => r.user_id === currentUserId);
  const isCreator = (d?.created_by_id || d?.uploaded_by_id) === currentUserId;
  // The feedback list is shown to the reviewer / creator / MA — never on
  // the public feed once a doc is approved.
  const showFeedback = !isPublished && (isMaster || iAmReviewer || isCreator);
  // The composer + revise/good-to-go actions live in the Review Doc sub-tab.
  const canFileFeedback = !isPublished && context === "review" && (isMaster || iAmReviewer);

  const annotationsFor = (targetType, targetIndex = null) => {
    if (!showFeedback) return [];
    return (d.review_annotations || []).filter((a) => {
      if ((a.target_type || "description") !== targetType) return false;
      if (targetType === "attachment") return Number(a.target_index) === Number(targetIndex);
      return true;
    });
  };

  const trackOpen = (i) => { api.post(`/documents/${d.id}/track-open`, { att_index: i }).catch(() => {}); };

  const download = async (i) => {
    trackOpen(i);
    try {
      const r = await api.get(`/documents/${d.id}/attachment/${i}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.attachments[i].filename || d.title; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
  };

  const viewFile = async (i) => {
    trackOpen(i);
    const w = window.open("", "_blank");
    try {
      const r = await api.get(`/documents/${d.id}/attachment/${i}/download?inline=true`, { responseType: "blob" });
      const type = d.attachments[i].content_type || r.data.type;
      const url = URL.createObjectURL(new Blob([r.data], { type }));
      if (w) w.location.href = url; else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (w) w.close();
      toast.error(formatError(e.response?.data?.detail || e.message));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="doc-detail-dialog">
        <DialogHeader>
          {/* Eyebrow — Type of Doc · Important-or-Not + status pills */}
          <div className="flex flex-wrap items-center gap-1.5 text-[11px]" data-testid="doc-detail-eyebrow">
            <span className="text-slate-500 dark:text-slate-400 uppercase tracking-wider font-bold">Type of Doc:</span>
            <span className={`inline-flex items-center gap-1 uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 ${tagMeta.cls}`}>{tagLabel}</span>
            <span className="text-slate-400 dark:text-slate-500">|</span>
            {d.is_important ? (
              <span className="inline-flex items-center gap-0.5 uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 bg-rose-600 text-white border-rose-700"><ShieldAlert className="w-2.5 h-2.5" /> Important</span>
            ) : (
              <span className="inline-flex items-center gap-0.5 uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700">Not Important</span>
            )}
            <span className={`inline-flex items-center gap-1 uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 ml-1 ${sMeta.cls}`}>
              <SIcon className="w-2.5 h-2.5" /> {sMeta.label}
            </span>
            {(d.revision_count || 0) > 0 && (
              <span className="inline-flex items-center gap-1 uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 bg-indigo-50 text-indigo-800 border-indigo-300 dark:bg-indigo-950/40 dark:text-indigo-200 dark:border-indigo-800">
                <RefreshCcw className="w-2.5 h-2.5" /> Re-Edited · {d.revision_count}x
                {d.last_revised_at && <span className="text-slate-500 dark:text-slate-400 normal-case ml-1 font-normal">· {fmtDateTimeIST(d.last_revised_at)}</span>}
              </span>
            )}
            {d.revise_pending && (
              <span className="inline-flex items-center gap-1 uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 bg-amber-500 text-white border-amber-600 animate-pulse" data-testid="doc-detail-review-comments-tag">
                <MessageSquare className="w-2.5 h-2.5" /> Review Comments Submitted
              </span>
            )}
          </div>
          {/* Sr.-No badge to make labelling obvious to the user */}
          <DialogTitle className="sr-only">{d.title}</DialogTitle>
          <DialogDescription className="sr-only">{d.description || d.title}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {/* TITLE (labelled) — reviewer feedback for the title lands right underneath */}
          <div data-testid="doc-detail-title-block">
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-indigo-700 dark:text-indigo-300 mb-1">Title</div>
            <div className="text-[16px] font-extrabold text-slate-900 dark:text-slate-100 flex items-start gap-2">
              {d.is_important && <ShieldAlert className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />}
              <span>{d.title}</span>
            </div>
            <FeedbackList notes={annotationsFor("title")} onDelete={showFeedback ? async (id) => {
              try { const { data } = await api.delete(`/documents/${d.id}/annotations/${id}`); setFull(data); toast.success("Removed"); }
              catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
            } : null} currentUserId={currentUserId} isMaster={isMaster} />
          </div>

          {/* DESCRIPTION (labelled) — reviewer feedback for description underneath */}
          {d.description && (
            <div data-testid="doc-detail-description-block">
              <div className="text-[10.5px] uppercase tracking-wider font-bold text-indigo-700 dark:text-indigo-300 mb-1">Description</div>
              <div className="whitespace-pre-wrap text-slate-700 dark:text-slate-200 text-[13px] leading-relaxed">{d.description}</div>
              <FeedbackList notes={annotationsFor("description")} onDelete={showFeedback ? async (id) => {
                try { const { data } = await api.delete(`/documents/${d.id}/annotations/${id}`); setFull(data); toast.success("Removed"); }
                catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
              } : null} currentUserId={currentUserId} isMaster={isMaster} />
            </div>
          )}

          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-indigo-700 dark:text-indigo-300 mb-1">Links &amp; Docs · {d.attachments?.length || 0}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="doc-detail-attachments">
              {(d.attachments || []).map((a, i) => {
                const { Icon, cls } = attachmentFlavor(a);
                const attFeedback = annotationsFor("attachment", i);
                return (
                  <div key={i} className="border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-900" data-testid={`doc-detail-attachment-${i}`}>
                    <div className="flex items-center gap-2 px-3 py-2">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${cls}`}><Icon size={14} /></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-semibold text-slate-800 dark:text-slate-200 truncate">
                          {a.type === "url" ? (a.hostname || a.external_url) : a.filename}
                        </div>
                        <div className="text-[10.5px] text-slate-500 dark:text-slate-400 truncate">
                          {a.type === "url" ? a.external_url : `${fmtSize(a.size)} · ${a.content_type || "file"}`}
                        </div>
                      </div>
                      {a.type === "url" ? (
                        <a href={a.external_url} target="_blank" rel="noopener noreferrer" onClick={() => trackOpen(i)} data-testid={`doc-detail-open-link-${i}`}><Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700 text-white"><ExternalLink size={11} className="mr-1" /> Open</Button></a>
                      ) : (
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => viewFile(i)} className="h-8 border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:text-indigo-200 dark:border-indigo-700" data-testid={`doc-detail-view-${i}`}><Eye size={11} className="mr-1" /> View</Button>
                          <Button size="sm" onClick={() => download(i)} className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`doc-detail-download-${i}`}><Download size={11} className="mr-1" /> Download</Button>
                        </div>
                      )}
                    </div>
                    {a.description && (
                      <div className="px-3 pb-2 pt-1.5 text-[11.5px] text-slate-600 dark:text-slate-400 border-t border-slate-100 dark:border-slate-800">
                        {a.description}
                      </div>
                    )}
                    {attFeedback.length > 0 && (
                      <div className="px-3 pb-2 pt-1.5 border-t border-slate-100 dark:border-slate-800">
                        <FeedbackList notes={attFeedback} onDelete={showFeedback ? async (id) => {
                          try { const { data } = await api.delete(`/documents/${d.id}/annotations/${id}`); setFull(data); toast.success("Removed"); }
                          catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
                        } : null} currentUserId={currentUserId} isMaster={isMaster} compact />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-indigo-700 mb-1">Audience</div>
              <div className="text-[12px] text-slate-700 dark:text-slate-300 flex flex-wrap items-center gap-1">
                {d.target_all ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-bold"><Globe2 size={12} /> Every enrolled employee</span>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1 text-amber-700 font-bold"><UsersIcon size={12} /> {(d.target_user_names || []).length} user(s)</span>
                    {(d.target_user_names || []).slice(0, 6).map((n, i) => (
                      <span key={i} className="rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2 py-0.5 text-[10.5px]">{n}</span>
                    ))}
                    {(d.target_user_names || []).length > 6 && (
                      <span className="text-[10.5px] text-slate-500">+{d.target_user_names.length - 6} more</span>
                    )}
                  </>
                )}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider font-bold text-indigo-700 mb-1">Timeline</div>
              <div className="text-[11.5px] text-slate-700 dark:text-slate-300 space-y-0.5">
                <div>Created: <span className="font-mono">{fmtDateTimeIST(d.created_at)}</span> by <strong>{d.original_creator_name || d.created_by_name}</strong></div>
                {d.published_at && <div>Published: <span className="font-mono">{fmtDateTimeIST(d.published_at)}</span></div>}
                {d.scheduled_for && d.status === "scheduled" && (
                  <div>Scheduled for: <span className="font-mono">{fmtDateTimeIST(d.scheduled_for)}</span> (<Clock className="inline w-3 h-3" /> {fmtCountdown(d.seconds_left_to_schedule)} left)</div>
                )}
                {d.end_at && <div>Expires: <span className="font-mono">{fmtDateTimeIST(d.end_at)}</span> · <em>{d.expiry_action}</em></div>}
                {d.reassigned_at && <div>Reassigned to <strong>{d.created_by_name}</strong> on <span className="font-mono">{fmtDateTimeIST(d.reassigned_at)}</span> by {d.reassigned_by_name}</div>}
                {d.last_revised_at && <div>Last revised: <span className="font-mono">{fmtDateTimeIST(d.last_revised_at)}</span> by <strong>{d.last_revised_by_name}</strong></div>}
              </div>
            </div>
          </div>

          <EditHistory doc={d} />

          {/* Rejection banner */}
          {d.status === "rejected" && d.rejection_remarks && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
              <div className="font-semibold flex items-center gap-1"><XCircle className="w-3 h-3" /> Rejected by {d.rejected_by_name}</div>
              <div className="mt-0.5">&ldquo;{d.rejection_remarks}&rdquo;</div>
            </div>
          )}

          {/* Review feedback composer + action buttons — Review Doc tab only. Per-field notes are shown inline above. */}
          <ReviewFeedbackComposer
            doc={d}
            currentUserId={currentUserId}
            isMaster={isMaster}
            visible={canFileFeedback}
            onRefresh={(fresh) => setFull(fresh)}
          />

          {/* Revise-pending banner for the Editor */}
          {isCreator && d.revise_pending && (
            <div className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-[12px] text-amber-900 dark:text-amber-200" data-testid="doc-revise-pending-banner">
              <div className="font-semibold flex items-center gap-1"><RefreshCcw className="w-3.5 h-3.5" /> Reviewer requested changes</div>
              <div className="mt-0.5">Please address the reviewer feedback shown above and re-submit for review.</div>
            </div>
          )}

          {/* Company Policy — Master Admin sees who accepted (name + date/time) */}
          {isMaster && d.tag === "company_policy" && (
            <PolicyAcksSection doc={d} />
          )}

          {/* User Comments — only visible when the doc is opened from the
              "Current" sub-tab (public feed). Hidden in Editor Corner
              (mine / approval / review / unassigned), Archived, and
              on Company Policy docs. */}
          {context === "current" && d.tag !== "company_policy" && (
            <CommentsSection doc={d} currentUserId={currentUserId} isMaster={isMaster} users={users} onRefresh={(fresh) => setFull(fresh)} />
          )}

          {d.can_view_reads !== false && <OpenedBy doc={d} />}
        </div>

        <DialogFooter className="flex-wrap gap-2 items-center" data-testid="doc-detail-footer">
          {/* Edit / Delete — access-gated. Editor can always fix a revise-pending doc. */}
          {onEdit && (isMaster || (isCreator && (d.status === "draft" || d.status === "scheduled" || d.status === "rejected" || d.revise_pending))) && (
            <Button
              variant="outline"
              onClick={() => { onEdit(d); onClose(); }}
              className="h-9 border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:text-indigo-200 dark:border-indigo-700 dark:hover:bg-indigo-950/40"
              data-testid="doc-detail-edit"
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> {d.revise_pending ? "Re-edit & Resubmit" : "Edit"}
            </Button>
          )}
          {onRemove && (isMaster || (isCreator && d.status === "draft")) && (
            <Button
              variant="outline"
              onClick={() => { onRemove(d); onClose(); }}
              className="h-9 border-rose-300 text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:border-rose-700 dark:hover:bg-rose-950/40"
              data-testid="doc-detail-delete"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
            </Button>
          )}
          <div className="flex-1" />
          <IReadButton doc={d} currentUserId={currentUserId} onRefresh={(fresh) => setFull(fresh)} />
          <Button variant="outline" onClick={onClose} data-testid="doc-detail-close">Close</Button>
        </DialogFooter>
        {busy && (
          <div className="pointer-events-none absolute top-2 right-2 text-[10.5px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5 shadow-sm">
            Loading…
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export { X };

// ================== "I READ" / "I ACCEPT" ACTION ==================

function IReadButton({ doc, currentUserId, onRefresh }) {
  const [showAccept, setShowAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!doc) return null;
  if (doc.status !== "approved") return null;
  const isCreator = (doc.created_by_id || doc.uploaded_by_id) === currentUserId;
  if (isCreator) return null;
  const isPolicy = doc.tag === "company_policy";

  // For non-policy docs: simple "I Read" (idempotent — server already marks
  // read on open, this is an explicit acknowledgement UI action).
  const markRead = async () => {
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/mark-read`);
      const { data } = await api.get(`/documents/${doc.id}?mark_read=false`);
      onRefresh?.(data);
      toast.success("Marked as read");
    } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  // For company_policy docs: mandatory pledge acceptance.
  const acceptPledge = async () => {
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/acknowledge`, {});
      const { data } = await api.get(`/documents/${doc.id}?mark_read=false`);
      onRefresh?.(data);
      toast.success("Policy accepted");
      setShowAccept(false);
    } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  if (isPolicy) {
    if (doc.i_acknowledged) {
      return (
        <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-full px-3 py-1.5">
          <CheckCircle2 className="w-3.5 h-3.5" /> Accepted
        </span>
      );
    }
    return (
      <>
        <Button
          onClick={() => setShowAccept(true)}
          className="bg-rose-600 hover:bg-rose-700 text-white"
          data-testid="doc-i-accept-btn"
        >
          <ShieldAlert className="w-4 h-4 mr-1.5" /> I Accept Policy
        </Button>
        <PolicyAcceptDialog
          open={showAccept}
          doc={doc}
          onClose={() => setShowAccept(false)}
          onAccept={acceptPledge}
          busy={busy}
        />
      </>
    );
  }

  if (doc.i_read) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-full px-3 py-1.5">
        <CheckCircle2 className="w-3.5 h-3.5" /> Read
      </span>
    );
  }
  return (
    <Button onClick={markRead} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="doc-i-read-btn">
      <CheckCircle2 className="w-4 h-4 mr-1.5" /> {busy ? "Saving…" : "I Read"}
    </Button>
  );
}

function PolicyAcceptDialog({ open, doc, onClose, onAccept, busy }) {
  const [pledge, setPledge] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get("/documents/policy/pledge-text").then((r) => setPledge(r.data?.pledge || "")).catch(() => setPledge(""));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="doc-policy-accept-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700 dark:text-rose-400">
            <ShieldAlert className="w-5 h-5" /> Accept Company Policy
          </DialogTitle>
          <DialogDescription>
            <strong>{doc.title}</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50/60 dark:bg-rose-950/30 px-3 py-2.5 text-[12.5px] leading-relaxed text-rose-900 dark:text-rose-200">
          {pledge || "Loading…"}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={onAccept} disabled={busy || !pledge} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="doc-policy-accept-submit">
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> {busy ? "Recording…" : "I Read & I Accept"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ================== REVIEWER FEEDBACK — display & composer ==================
//
// Feedback is captured per-field (target_type = title | description |
// attachment) and rendered INLINE right underneath each field so the
// editor can see exactly what to change. The composer + Submit/Approve
// actions live in the Editor Corner → Review Doc sub-tab.

const TARGET_LABEL = {
  title: "Title",
  description: "Description",
  attachment: "Attachment",
};

// Small inline strip: shows the reviewer's Issue, Change required + optional
// quote / comment for a specific field. Used under each field block.
function FeedbackList({ notes = [], onDelete, currentUserId, isMaster, compact = false }) {
  if (!notes || notes.length === 0) return null;
  return (
    <div className={`mt-2 space-y-1.5 ${compact ? "" : "border-l-2 border-violet-300 dark:border-violet-700 pl-3"}`} data-testid="doc-feedback-list">
      <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700 dark:text-violet-300 flex items-center gap-1">
        <Highlighter className="w-2.5 h-2.5" /> Reviewer feedback · {notes.length}
      </div>
      {notes.map((a) => {
        const col = HIGHLIGHT_COLORS.find((c) => c.key === a.color) || HIGHLIGHT_COLORS[0];
        const mine = a.reviewer_id === currentUserId;
        return (
          <div key={a.id} className={`border rounded-md px-2.5 py-1.5 ${col.cls}`} data-testid={`doc-annotation-${a.id}`}>
            <div className="flex items-center justify-between gap-2 mb-0.5">
              <span className="text-[11px] font-semibold">{a.reviewer_name}</span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono opacity-70">{fmtDateTimeIST(a.at)}</span>
                {onDelete && (mine || isMaster) && (
                  <button onClick={() => { if (window.confirm("Delete this feedback?")) onDelete(a.id); }} className="opacity-70 hover:opacity-100" title="Remove"><Trash2 className="w-3 h-3" /></button>
                )}
              </div>
            </div>
            {a.issue && <div className="text-[12px]"><strong className="uppercase text-[9.5px] tracking-wider">Issue:</strong> {a.issue}</div>}
            {a.changes_needed && <div className="text-[12px]"><strong className="uppercase text-[9.5px] tracking-wider">Change required:</strong> {a.changes_needed}</div>}
            {a.quote && <div className="text-[11.5px] mt-0.5 italic">&ldquo;{a.quote}&rdquo;</div>}
            {a.comment && <div className="text-[12px] mt-0.5 flex items-start gap-1"><MessageSquare className="w-3 h-3 mt-0.5" />{a.comment}</div>}
          </div>
        );
      })}
    </div>
  );
}

// Composer + Submit-for-Revision / Good-to-Go / Reject action panel. Only
// shown inside the Editor Corner → Review Doc sub-tab.
function ReviewFeedbackComposer({ doc, currentUserId, isMaster, visible, onRefresh }) {
  const [targetType, setTargetType] = useState("description");
  const [targetIndex, setTargetIndex] = useState(0);
  const [issue, setIssue] = useState("");
  const [changesNeeded, setChangesNeeded] = useState("");
  const [quote, setQuote] = useState("");
  const [comment, setComment] = useState("");
  const [color, setColor] = useState("yellow");
  const [busy, setBusy] = useState(false);
  const [reviseRemarks, setReviseRemarks] = useState("");

  const attachments = doc?.attachments || [];
  const reviewers = doc?.reviewers || [];
  const myReviewer = reviewers.find((r) => r.user_id === currentUserId);
  const iHaveApproved = !!myReviewer?.good_to_go;

  if (!visible) return null;

  const add = async () => {
    if (!issue.trim() && !changesNeeded.trim() && !quote.trim() && !comment.trim()) {
      toast.error("Describe the issue or the change needed");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        target_type: targetType,
        target_index: targetType === "attachment" ? Number(targetIndex) : null,
        issue: issue.trim(),
        changes_needed: changesNeeded.trim(),
        quote: quote.trim(),
        comment: comment.trim(),
        color,
      };
      const { data } = await api.post(`/documents/${doc.id}/annotations`, payload);
      setIssue(""); setChangesNeeded(""); setQuote(""); setComment("");
      onRefresh?.(data);
      toast.success("Feedback filed");
    } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const sendForRevision = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/reviewers/revise`, { remarks: reviseRemarks.trim() });
      setReviseRemarks("");
      onRefresh?.(data);
      toast.success("Sent back to editor for revision");
    } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const approveGoodToGo = async () => {
    if (!window.confirm("Approve this document? It will move forward for Master Admin approval once all reviewers approve.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/reviewers/good-to-go`);
      onRefresh?.(data);
      toast.success("Marked as Good to Go");
    } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/25 p-3 space-y-3" data-testid="doc-review-composer">
      <div className="text-[11px] uppercase tracking-wider font-bold text-violet-700 dark:text-violet-300 flex items-center gap-1">
        <Highlighter className="w-3 h-3" /> File reviewer feedback
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300">Against:</span>
        <select
          value={targetType}
          onChange={(e) => setTargetType(e.target.value)}
          className="h-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-[12px] px-2"
          data-testid="doc-annotation-target-type"
        >
          <option value="title">Title</option>
          <option value="description">Description</option>
          {attachments.length > 0 && <option value="attachment">Specific Link / Doc</option>}
        </select>
        {targetType === "attachment" && (
          <select
            value={targetIndex}
            onChange={(e) => setTargetIndex(Number(e.target.value))}
            className="h-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-[12px] px-2 max-w-[240px]"
            data-testid="doc-annotation-target-index"
          >
            {attachments.map((a, i) => (
              <option key={i} value={i}>
                {i + 1}. {a.type === "url" ? (a.hostname || a.external_url) : a.filename}
              </option>
            ))}
          </select>
        )}
      </div>

      <Input value={issue} onChange={(e) => setIssue(e.target.value)} placeholder="Exact issue (e.g., wording unclear, wrong number, broken link)" data-testid="doc-annotation-issue" />
      <Input value={changesNeeded} onChange={(e) => setChangesNeeded(e.target.value)} placeholder="Change required — what should the editor change?" data-testid="doc-annotation-changes" />
      <Input value={quote} onChange={(e) => setQuote(e.target.value)} placeholder="Highlight text (optional — paste the sentence you're referring to)" data-testid="doc-annotation-quote" />
      <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Additional comment (optional)" data-testid="doc-annotation-comment" />

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          {HIGHLIGHT_COLORS.map((c) => (
            <button key={c.key} type="button" onClick={() => setColor(c.key)}
              className={`w-6 h-6 rounded-full border-2 ${c.swatch} ${color === c.key ? "border-slate-800 ring-2 ring-slate-800/20" : "border-white"}`}
              aria-label={c.key} title={c.key} data-testid={`doc-annotation-color-${c.key}`} />
          ))}
        </div>
        <Button onClick={add} disabled={busy} className="h-8 bg-violet-600 hover:bg-violet-700 text-white" data-testid="doc-annotation-add">
          <Highlighter className="w-3.5 h-3.5 mr-1" /> {busy ? "Saving…" : "Add feedback"}
        </Button>
      </div>

      {/* Reviewer decision buttons */}
      <div className="pt-3 border-t border-violet-200/70 dark:border-violet-800/50 space-y-2">
        <div className="text-[10.5px] uppercase tracking-wider font-bold text-violet-700 dark:text-violet-300">Reviewer decision</div>
        <Input
          value={reviseRemarks}
          onChange={(e) => setReviseRemarks(e.target.value)}
          placeholder="Short note for the editor (optional)"
          data-testid="doc-review-revise-remarks"
        />
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <Button
            onClick={sendForRevision}
            disabled={busy}
            variant="outline"
            className="h-8 border-amber-400 text-amber-800 hover:bg-amber-50 dark:text-amber-200 dark:border-amber-700 dark:hover:bg-amber-900/30"
            data-testid="doc-review-submit-revise"
          >
            <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Submit — send back to Editor
          </Button>
          {(myReviewer || isMaster) && (
            <Button
              onClick={approveGoodToGo}
              disabled={busy || iHaveApproved}
              className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="doc-review-approve"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {iHaveApproved ? "Approved" : "Approve — Good to Go"}
            </Button>
          )}
        </div>
        <div className="text-[10.5px] text-slate-500 dark:text-slate-400 italic">
          Submitting sends the doc back to the editor to make the changes above. Approving moves it forward for Master Admin approval (once every reviewer approves).
        </div>
      </div>
    </div>
  );
}

// ================== POLICY ACKS (Master Admin audit) ==================
//
// For company_policy docs, Master Admin sees the full list of employees
// who clicked "I Accept" with their name + date/time. Outside the
// dialog (on cards / list rows) we only surface the count.

function PolicyAcksSection({ doc }) {
  const acks = doc?.policy_acks || [];
  return (
    <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50/40 dark:bg-rose-950/20 p-3" data-testid="doc-policy-acks">
      <div className="text-[11px] uppercase tracking-wider font-bold text-rose-700 dark:text-rose-300 flex items-center gap-1 mb-1.5">
        <ShieldAlert className="w-3 h-3" /> Policy acceptances · {acks.length}
      </div>
      {acks.length === 0 ? (
        <div className="text-[11.5px] text-slate-500 dark:text-slate-400 italic">No employee has accepted this policy yet.</div>
      ) : (
        <div className="max-h-40 overflow-y-auto border border-rose-200 dark:border-rose-800 rounded-md divide-y divide-rose-100 dark:divide-rose-900 bg-white/60 dark:bg-slate-900/40">
          {acks.map((a, i) => (
            <div key={i} className="px-2.5 py-1.5 text-[11.5px] flex items-center justify-between gap-2">
              <span className="font-semibold text-slate-800 dark:text-slate-200 truncate">{a.name || "—"}</span>
              <span className="font-mono text-slate-500 dark:text-slate-400">{fmtDateTimeIST(a.at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ================== USER COMMENTS (typed + tag + reply) ==================
//
// Any user in the audience (plus the creator and reviewers) can add a
// comment of type: feedback / error / changes_needed / clarification /
// doubt. They can @-tag any active user. Tagged users, the author and
// Master Admin can reply. Comments are visible to everyone with access
// to the doc (already enforced server-side). Master Admin & the comment
// author can delete a comment.

const COMMENT_TYPES = [
  { key: "feedback",        label: "Feedback",         Icon: MessageSquare, cls: "bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-950/50 dark:text-indigo-200 dark:border-indigo-800" },
  { key: "error",           label: "Error",            Icon: XCircle,       cls: "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950/50 dark:text-rose-200 dark:border-rose-800" },
  { key: "changes_needed",  label: "Changes Needed",   Icon: AlertTriangle, cls: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-800" },
  { key: "clarification",   label: "Clarification",    Icon: HelpCircle,    cls: "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-950/50 dark:text-sky-200 dark:border-sky-800" },
  { key: "doubt",           label: "Doubt",            Icon: HelpCircle,    cls: "bg-violet-100 text-violet-800 border-violet-300 dark:bg-violet-950/50 dark:text-violet-200 dark:border-violet-800" },
];

function CommentsSection({ doc, currentUserId, isMaster, users, onRefresh }) {
  const [type, setType] = useState("feedback");
  const [text, setText] = useState("");
  const [tagIds, setTagIds] = useState([]);
  const [tagQuery, setTagQuery] = useState("");
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const comments = doc?.comments || [];
  const activeUsers = useMemo(
    () => (users || []).filter((u) => u.id !== currentUserId),
    [users, currentUserId],
  );
  const filteredUsers = useMemo(() => {
    const q = tagQuery.trim().toLowerCase();
    if (!q) return activeUsers.slice(0, 10);
    return activeUsers.filter((u) => `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(q)).slice(0, 20);
  }, [activeUsers, tagQuery]);
  const taggedUsers = useMemo(
    () => tagIds.map((id) => (users || []).find((u) => u.id === id)).filter(Boolean),
    [tagIds, users],
  );

  const submit = async () => {
    if (!text.trim()) { toast.error("Type a comment"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/comments`, {
        text: text.trim(), type, tagged_user_ids: tagIds,
      });
      setText(""); setTagIds([]); setTagQuery(""); setShowTagPicker(false); setType("feedback");
      onRefresh?.(data);
      toast.success("Comment added");
    } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const reply = async (commentId) => {
    if (!replyText.trim()) { toast.error("Type your reply"); return; }
    try {
      const { data } = await api.post(`/documents/${doc.id}/comments/${commentId}/reply`, { text: replyText.trim() });
      setReplyingTo(null); setReplyText("");
      onRefresh?.(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
  };
  const remove = async (commentId) => {
    if (!window.confirm("Delete this comment (and all its replies)?")) return;
    try {
      const { data } = await api.delete(`/documents/${doc.id}/comments/${commentId}`);
      onRefresh?.(data);
      toast.success("Deleted");
    } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
  };
  const toggleTag = (uid) => {
    setTagIds((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };
  const typeMeta = (k) => COMMENT_TYPES.find((t) => t.key === k) || COMMENT_TYPES[0];

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/60 p-3 space-y-2.5" data-testid="doc-comments">
      <div className="text-[11px] uppercase tracking-wider font-bold text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
        <MessageSquare className="w-3 h-3" /> Comments · {comments.length}
      </div>

      {comments.length === 0 && (
        <div className="text-[11.5px] text-slate-500 dark:text-slate-400 italic">No comments yet. Be the first to share feedback.</div>
      )}
      {comments.length > 0 && (
        <div className="space-y-2">
          {comments.map((c) => {
            const meta = typeMeta(c.type);
            const CIcon = meta.Icon;
            const canDelete = isMaster || c.user_id === currentUserId;
            return (
              <div key={c.id} className="border border-slate-200 dark:border-slate-700 rounded-md bg-slate-50/60 dark:bg-slate-900/40" data-testid={`doc-comment-${c.id}`}>
                <div className="px-2.5 py-1.5 flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 ${meta.cls}`}>
                        <CIcon className="w-2.5 h-2.5" /> {meta.label}
                      </span>
                      <span className="text-[11.5px] font-semibold text-slate-800 dark:text-slate-200">{c.user_name}</span>
                      {c.user_role && <span className="text-[10px] text-slate-500 dark:text-slate-400">· {c.user_role.replace(/_/g, " ")}</span>}
                    </div>
                    <div className="text-[12.5px] text-slate-700 dark:text-slate-200 mt-1 whitespace-pre-wrap break-words">{c.text}</div>
                    {(c.tagged_user_names || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {c.tagged_user_names.map((n, i) => (
                          <span key={i} className="inline-flex items-center gap-0.5 text-[10.5px] font-semibold text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40 border border-sky-200 dark:border-sky-800 rounded-full px-1.5 py-0.5">
                            <AtSign className="w-2.5 h-2.5" />{n}
                          </span>
                        ))}
                      </div>
                    )}
                    {(c.replies || []).length > 0 && (
                      <div className="mt-2 pl-3 border-l-2 border-slate-200 dark:border-slate-700 space-y-1.5">
                        {c.replies.map((r) => (
                          <div key={r.id} className="text-[12px]" data-testid={`doc-comment-reply-${r.id}`}>
                            <div className="flex items-center gap-1 text-[10.5px] text-slate-500 dark:text-slate-400">
                              <CornerDownRight className="w-3 h-3" />
                              <strong className="text-slate-700 dark:text-slate-200">{r.user_name}</strong>
                              <span className="font-mono ml-auto">{fmtDateTimeIST(r.at)}</span>
                            </div>
                            <div className="text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words pl-4">{r.text}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Reply composer */}
                    {replyingTo === c.id ? (
                      <div className="mt-2 flex items-center gap-1.5">
                        <Input
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Type your reply…"
                          className="h-8 text-[12px]"
                          data-testid={`doc-comment-reply-input-${c.id}`}
                        />
                        <Button size="sm" onClick={() => reply(c.id)} className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-[11px]" data-testid={`doc-comment-reply-send-${c.id}`}>Send</Button>
                        <Button size="sm" variant="outline" onClick={() => { setReplyingTo(null); setReplyText(""); }} className="h-8 text-[11px]">Cancel</Button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setReplyingTo(c.id); setReplyText(""); }}
                        className="mt-1.5 text-[10.5px] text-indigo-600 dark:text-indigo-400 hover:underline inline-flex items-center gap-1"
                        data-testid={`doc-comment-reply-btn-${c.id}`}
                      >
                        <CornerDownRight className="w-3 h-3" /> Reply
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-[10px] font-mono text-slate-400 dark:text-slate-500">{fmtDateTimeIST(c.at)}</span>
                    {canDelete && (
                      <button onClick={() => remove(c.id)} className="text-slate-400 hover:text-rose-600" title="Delete comment" data-testid={`doc-comment-delete-${c.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Composer */}
      <div className="pt-2 border-t border-slate-200 dark:border-slate-700 space-y-2">
        <div className="text-[10.5px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">Add a comment</div>
        <div className="flex flex-wrap gap-1.5" data-testid="doc-comment-type-chips">
          {COMMENT_TYPES.map((t) => {
            const TIcon = t.Icon;
            const active = type === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setType(t.key)}
                className={`inline-flex items-center gap-1 text-[10.5px] uppercase tracking-wider font-bold border rounded-full px-2 py-1 ${active ? t.cls + " ring-2 ring-current/40" : "bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-400"}`}
                data-testid={`doc-comment-type-${t.key}`}
              >
                <TIcon className="w-3 h-3" /> {t.label}
              </button>
            );
          })}
        </div>

        <Textarea
          rows={2}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write your comment. Tag colleagues below so they get notified."
          data-testid="doc-comment-text"
        />

        {/* Tag picker */}
        <div>
          <button
            type="button"
            onClick={() => setShowTagPicker((s) => !s)}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
            data-testid="doc-comment-toggle-tag"
          >
            <AtSign className="w-3 h-3" /> Tag users {tagIds.length ? `(${tagIds.length})` : ""}
          </button>
          {taggedUsers.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {taggedUsers.map((u) => (
                <span key={u.id} className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-950/40 border border-sky-200 dark:border-sky-800 rounded-full px-1.5 py-0.5">
                  <AtSign className="w-2.5 h-2.5" />{u.name}
                  <button onClick={() => toggleTag(u.id)} className="hover:text-rose-500"><X className="w-2.5 h-2.5" /></button>
                </span>
              ))}
            </div>
          )}
          {showTagPicker && (
            <div className="mt-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 space-y-1.5">
              <div className="relative">
                <SearchIcon className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={tagQuery}
                  onChange={(e) => setTagQuery(e.target.value)}
                  placeholder="Search users…"
                  className="h-8 pl-7 text-[12px]"
                  data-testid="doc-comment-tag-search"
                />
              </div>
              <div className="max-h-40 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800" data-testid="doc-comment-tag-list">
                {filteredUsers.length === 0 && (
                  <div className="text-[11.5px] text-slate-400 dark:text-slate-500 italic py-2 text-center">No users found</div>
                )}
                {filteredUsers.map((u) => {
                  const picked = tagIds.includes(u.id);
                  return (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => toggleTag(u.id)}
                      className={`w-full text-left px-2 py-1.5 flex items-center justify-between gap-2 text-[12px] rounded ${picked ? "bg-indigo-50 dark:bg-indigo-950/40" : "hover:bg-slate-50 dark:hover:bg-slate-800/40"}`}
                      data-testid={`doc-comment-tag-user-${u.id}`}
                    >
                      <span className="min-w-0 flex-1 truncate">
                        <strong className="text-slate-800 dark:text-slate-200">{u.name}</strong>
                        <span className="text-slate-400 dark:text-slate-500 ml-1">· {u.email}</span>
                      </span>
                      {picked && <CheckCircle2 className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end">
          <Button onClick={submit} disabled={busy || !text.trim()} className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="doc-comment-submit">
            <MessageSquare className="w-3.5 h-3.5 mr-1" /> {busy ? "Posting…" : "Post comment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditHistory({ doc }) {
  const hist = doc.edit_history || [];
  return (
    <div data-testid="doc-detail-edit-history">
      <div className="text-[11px] uppercase tracking-wider font-bold text-indigo-700 mb-1 flex items-center gap-1">
        <Pencil className="w-3 h-3" /> Last Updated &amp; What Changed
      </div>
      {hist.length === 0 ? (
        <div className="text-[11.5px] text-slate-500 dark:text-slate-400 italic" data-testid="doc-detail-no-edits">
          Not edited since it was created{doc.updated_at ? ` · last saved ${fmtDateTimeIST(doc.updated_at)}` : ""}.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {hist.map((h, i) => (
            <div key={i} className={`text-[11.5px] rounded-md px-2.5 py-1.5 border ${i === 0 ? "bg-indigo-50 border-indigo-200 dark:bg-indigo-950/40 dark:border-indigo-800" : "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700"}`} data-testid={`doc-edit-entry-${i}`}>
              <div className="flex items-center gap-1.5">
                {i === 0 && <span className="text-[9.5px] uppercase font-bold tracking-wider text-indigo-700 dark:text-indigo-300">Last Updated</span>}
                <strong className="text-slate-800 dark:text-slate-200">{h.by_name}</strong>
                <span className="text-slate-400 font-mono ml-auto">{fmtDateTimeIST(h.at)}</span>
              </div>
              <ul className="list-disc pl-5 mt-0.5 text-slate-600 dark:text-slate-300">
                {(h.changes || []).map((c, j) => <li key={j} className="break-all">{c}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OpenedBy({ doc }) {
  const reads = doc.read_by || [];
  const atts = doc.attachments || [];
  const attName = (k) => {
    const a = atts[Number(k)];
    return a ? (a.type === "url" ? (a.hostname || a.external_url) : a.filename) : `#${Number(k) + 1}`;
  };
  return (
    <div data-testid="doc-detail-opened-by">
      <div className="text-[11px] uppercase tracking-wider font-bold text-indigo-700 mb-1 flex items-center gap-1">
        <Eye className="w-3 h-3" /> Opened by · {doc.read_count || 0} user(s) · {doc.total_opens || 0} open(s)
      </div>
      {reads.length === 0 ? (
        <div className="text-[11.5px] text-slate-500 dark:text-slate-400 italic">No one has opened this document since it was last updated.</div>
      ) : (
        <div className="max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-md divide-y dark:divide-slate-800" data-testid="doc-detail-readers">
          {reads.map((r, i) => (
            <div key={i} className="px-2.5 py-1.5 text-[11.5px]" data-testid={`doc-reader-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-slate-700 dark:text-slate-300 truncate">{r.name}</span>
                <span className="text-indigo-700 dark:text-indigo-300 font-bold" data-testid={`doc-reader-count-${i}`}>{r.open_count}x opened</span>
              </div>
              <div className="text-[10.5px] text-slate-500 dark:text-slate-400">
                First: <span className="font-mono">{fmtDateTimeIST(r.at)}</span> · Last: <span className="font-mono">{fmtDateTimeIST(r.last_at)}</span>
              </div>
              {Object.keys(r.attachment_opens || {}).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {Object.entries(r.attachment_opens).map(([k, n]) => (
                    <span key={k} className="rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2 py-0.5 text-[10px] max-w-[220px] truncate" title={attName(k)}>
                      {attName(k)} · {n}x
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
